import { Inject, Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ApiService } from '../../shared/api.service';
import { localStorageService } from '../../shared/localstorage.service';
import { PermissionsService } from '../../shared/permissions/permissions.service';

export type DashboardView  = 'my' | 'team' | 'L1' | 'L2';

export const STORAGE_KEY_VIEW = {
  VIEW : 'sales_dashboard_view',
  PROFILE : 'admin_profile'
}

const MANAGER_VIEW = {
  TEAM_L1: 'L1' as DashboardView,
  TEAM_L2: 'L2' as DashboardView
}

/**
 * Sales view service — capability-driven (no `role_enum` reads).
 *
 *   - `isTeamLead`  -> caller has `SALES.LEADS.VIEW_TEAM` and not `VIEW_ALL`
 *   - `isManager`   -> caller has `SALES.LEADS.VIEW_ALL` (or wildcard / super-admin)
 *
 * The default view persisted in localStorage is `'L1'` for managers and
 * `'my'` for everyone else, matching the previous role_enum-based behavior.
 *
 * `team_list` is fetched from `/admin/sales/team` only when the caller can
 * read it (TL or manager).
 */
@Injectable()
export class SalesViewService implements OnDestroy {
  private _dashboardView = new BehaviorSubject<DashboardView>('my');
  private _selectedAdminId = new BehaviorSubject<number | null>(null);
  private _teamList = new BehaviorSubject<{ admin_id: number; admin_name: string; admin_email: string; role_id?: number }[]>([]);
  private _isTeamLead = new BehaviorSubject<boolean>(false);
  private _isManager = new BehaviorSubject<boolean>(false);

  dashboardView$ = this._dashboardView.asObservable();
  selectedAdminId$ = this._selectedAdminId.asObservable();
  teamList$ = this._teamList.asObservable();
  isTeamLead$ = this._isTeamLead.asObservable();
  isManager$ = this._isManager.asObservable();
  profileData: any;

  private _permissionsSub?: Subscription;
  private _permissions = inject(PermissionsService);

  constructor(
    @Inject(localStorageService) private _local: localStorageService,
    private _api: ApiService
  ) {
    this.profileData = this._local.customGetItem(STORAGE_KEY_VIEW.PROFILE);
    this.syncFromCapabilities(this._permissions.isManager, this._permissions.isTeamLead);
    const stored = this.getStoredView();
    if (stored !== 'my') { this._dashboardView.next(stored as DashboardView); }

    // React to capability changes (e.g. /me/capabilities refresh).
    this._permissionsSub = this._permissions.snapshot$.subscribe(() => {
      this.syncFromCapabilities(this._permissions.isManager, this._permissions.isTeamLead);
    });
  }

  ngOnDestroy(): void {
    this._permissionsSub?.unsubscribe();
  }

  private getStoredView(): DashboardView {
    try {
      const stored = this._local.customGetItem(STORAGE_KEY_VIEW.VIEW);
      if (this._permissions.isManager) {
        return (stored as DashboardView) || MANAGER_VIEW.TEAM_L1;
      }
      if (stored === 'my' || stored === 'team') { return stored; }
    } catch (_) {}
    return this._permissions.isManager
      ? (MANAGER_VIEW.TEAM_L1 as DashboardView)
      : 'my';
  }

  get dashboardView(): DashboardView {
    return this._dashboardView.value;
  }

  get selectedAdminId(): number | null {
    return this._selectedAdminId.value;
  }

  get teamList(): { admin_id: number; admin_name: string; admin_email: string; role_id?: number }[] {
    return this._teamList.value;
  }

  get isTeamLead(): boolean {
    return this._isTeamLead.value;
  }

  get isManager(): boolean {
    return this._isManager.value;
  }

  get adminProfile(): any {
    return this._local.customGetItem('admin_profile');
  }

  /** Effective admin id for API: My View = current user; Team View = selected dropdown or null for all. */
  get effectiveSelectedAdminId(): number | null {
    if (this._dashboardView.value === 'my') { return this.adminProfile?.admin_id ?? null; }
    return this._selectedAdminId.value;
  }

  /**
   * Refresh role-derived flags from the current capability snapshot. Called on
   * construction and whenever PermissionsService emits.
   *
   * @deprecated Use `syncFromCapabilities` directly. The legacy
   * `syncFromProfile` name is kept for backwards compat with any external
   * callers; both paths now read capabilities.
   */
  syncFromProfile(): void {
    this.syncFromCapabilities(this._permissions.isManager, this._permissions.isTeamLead);
  }

  private syncFromCapabilities(isManager: boolean, isLead: boolean): void {
    const wasLead = this._isTeamLead.value;
    const wasManager = this._isManager.value;
    this._isTeamLead.next(isLead);
    this._isManager.next(isManager);
    if ((isLead || isManager) && (!wasLead && !wasManager)) {
      // Just gained team-list access -> hydrate.
      this.fetchTeamList();
    }
    if (!isLead && !isManager && (wasLead || wasManager)) {
      this._teamList.next([]);
    }
  }

  setDashboardView(view: DashboardView): void {
    if (this._dashboardView.value === view) { return; }
    this._dashboardView.next(view);
    try {
      this._local.customSetItem(STORAGE_KEY_VIEW.VIEW, view);
    } catch (_) {}
    // Resetting the per-admin selection only makes sense for non-manager
    // flows; managers track L1/L2 sub-views via `view_as`.
    if (!this._permissions.isManager && view === 'team') {
      this._selectedAdminId.next(null);
    }
  }

  setSelectedAdminId(id: number | null): void {
    this._selectedAdminId.next(id);
  }

  fetchTeamList(): void {
    this._api.api({}, 'sales/team', 'get').subscribe({
      next: (res: any) => this._teamList.next(res?.team ?? []),
      error: () => this._teamList.next([])
    });
  }
}
